import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { gtag } from '../../Analytics/gtag';
import PowerCheckoutForm from '../../PowerCheckoutForm';
import Img from '../../Img';
import config from '../../../../config';
import links from '../../../../config/links';
import useFabletics from '../../hooks/useFabletics';
import FableticsUpgradeCreative from '../../Fabletics/UpgradeCreative';
import Popup from '../../Popup/PopupV2';
import { showPopupNew, closePopup } from '../../../actions/popup-actions';
import { PopupTypes, stripePlans, SubscriptionIntervalTypes } from '../../../utils/constants';
import { getPowerSubscriptionPlans, getPowerSubscriptionDetails } from '../../../api/checkoutApi';
import { getApiAxiosInstance } from '../../../utils/network';
import { intervalToBillingCycle } from '../../Analytics/constants';
import { usePowerFunnelTracking } from '../../hooks/usePowerFunnelTracking';
import { getLeftColumnVariantFor } from '../leftColumns/registry';

import styles from './upgradeV4.scss';
import popupStyles from '../../Popup/popupv2.scss';

const SubscriptionIntervals = SubscriptionIntervalTypes;

const API_STATUS = {
    SUCCESS: 'success',
};

const FableticsVariant = {
    CONTROL: 'control',
    VERSION_TWO: 'version_two',
};

const UpgradeToPowerPopupV4 = (props) => {
    const { showHotelPlannerCard = true, popupType, referringSurface, gatedFeature, prismicTitle } = props || {};
    const showBothPlanOptions = showHotelPlannerCard; // When false, only annual plan is available
    const [interval, setInterval] = useState(SubscriptionIntervals.YEAR);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [couponCode, setCouponCode] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [invoiceItems, setInvoiceItems] = useState(null);
    const [isLoadingPricing, setIsLoadingPricing] = useState(true);
    const [binCountryError, setBinCountryError] = useState('');
    const [isTennisAustraliaMember, setIsTennisAustraliaMember] = useState(false);
    const popups = useSelector(({ popups }) => popups);
    const dispatch = useDispatch();
    const { enabled: isFableticsEnabled, variant } = useFabletics();
    const LeftColumnVariant = getLeftColumnVariantFor(gatedFeature);
    const isCppContext = !!LeftColumnVariant;

    const { trackPowerPaywallViewed, trackPowerPurchaseSucceeded } = usePowerFunnelTracking({ referringSurface, gatedFeature, prismicTitle });

    const fetchSubscriptionPlans = async () => {
        try {
            const response = await getPowerSubscriptionPlans(getApiAxiosInstance());
            if (response?.data?.subscriptionPlans) {
                setSubscriptionPlans(response.data.subscriptionPlans);
            }
            if (response?.data?.isTennisAustraliaMember) {
                setIsTennisAustraliaMember(true);
            }
        } catch (error) {
            setSubscriptionPlans([]);
        }
    };

    const isV2022Plan = (targetInterval = null, plansToUse = null) => {
        const intervalToUse = targetInterval || interval;
        const plansArray = plansToUse || subscriptionPlans;
        const selectedPlan = plansArray.find(plan => plan.interval === intervalToUse);
        const planId = selectedPlan?.planId;
        return planId === stripePlans.members.powerAnnual || planId === stripePlans.members.powerMonthly;
    };

    const fetchSubscriptionDetails = async (targetInterval = null, couponCodeParam = null, plansToUse = null) => {
        try {
            const intervalToUse = targetInterval || interval;
            const plansArray = plansToUse || subscriptionPlans;
            const selectedPlan = plansArray.find(plan => plan.interval === intervalToUse);
            const planId = selectedPlan?.planId;

            if (!planId) {
                if (couponCodeParam) {
                    setCouponError('Plan not available');
                }
                return null;
            }

            const apiParams = { plan: planId };
            if (couponCodeParam) {
                apiParams.couponCode = couponCodeParam;
            }

            const response = await getPowerSubscriptionDetails(getApiAxiosInstance(), apiParams);
            if (response.data.status === API_STATUS.SUCCESS) {
                if (couponCodeParam) {
                    setCouponError('');
                }
                return response.data.data;
            }

            if (couponCodeParam) {
                setCouponError(response.data.message || 'Invalid coupon code');
            }
            return null;
        } catch (error) {
            if (couponCodeParam) {
                setCouponError('Error applying coupon');
            }
            return null;
        }
    };

    useEffect(() => {
        fetchSubscriptionPlans();
        trackPowerPaywallViewed();
    }, []);

    useEffect(() => {
        const loadSubscriptionDetails = async () => {
            if (subscriptionPlans.length > 0) {
                setIsLoadingPricing(true);
                const requiresSubscriptionDetailsForV2022Plan = isV2022Plan();
                if (requiresSubscriptionDetailsForV2022Plan) {
                    const invoiceData = await fetchSubscriptionDetails();
                    setInvoiceItems(invoiceData);
                } else {
                    setInvoiceItems(null);
                }
                setIsLoadingPricing(false);
            }
        };
        loadSubscriptionDetails();
    }, [subscriptionPlans, interval]);

    const handleSuccess = () => {
        const planData = getSelectedPlan();
        const billingCycle = intervalToBillingCycle[planData?.interval] || null;

        trackPowerPurchaseSucceeded({
            billingCycle,
            priceLocal: planData?.amountPayPerRecurringInterval,
            currency: planData?.currency || 'USD',
        });

        gtag('power-upgrade', {
            category: 'Power Upgrade',
            context: showHotelPlannerCard ? 'Power Upgrade success' : 'Hotel Planner Upgrade success',
        });
        dispatch(closePopup(
            popupType || (showHotelPlannerCard ? PopupTypes.POWER_UPGRADE_POPUP : PopupTypes.UPGRADE_TO_POWER_POPUP),
        ));
        dispatch(showPopupNew({
            popup: PopupTypes.POWER_SUCCESS_POPUP_V2,
            redirectTo: popups.redirectTo,
        }));
    };

    const handleBinCountryMismatch = async (availablePowerPlans, errorMessage) => {
        setIsLoadingPricing(true);
        if (availablePowerPlans.subscriptionPlans && availablePowerPlans.subscriptionPlans.length > 0) {
            setSubscriptionPlans(availablePowerPlans.subscriptionPlans);
        }
        if (availablePowerPlans.isTennisAustraliaMember !== undefined) {
            setIsTennisAustraliaMember(availablePowerPlans.isTennisAustraliaMember);
        }
        setBinCountryError(errorMessage);
        setCouponCode(null);
        setCouponError('');

        const newPlans = availablePowerPlans.subscriptionPlans || subscriptionPlans;
        const requiresSubscriptionDetailsForV2022Plan = isV2022Plan(interval, newPlans);

        if (requiresSubscriptionDetailsForV2022Plan) {
            const invoiceData = await fetchSubscriptionDetails(interval, null, newPlans);
            setInvoiceItems(invoiceData);
        } else {
            setInvoiceItems(null);
        }
        setIsLoadingPricing(false);
    };

    const applyCoupon = async (couponCodeParam) => {
        if (!couponCodeParam || couponCodeParam.trim() === '') {
            setCouponError('Please enter a coupon code');
            return;
        }

        const isV2022 = isV2022Plan();
        if (isV2022) {
            const invoiceData = await fetchSubscriptionDetails(null, couponCodeParam.trim());
            setInvoiceItems(invoiceData);
            if (invoiceData) {
                setCouponCode(couponCodeParam.trim());
            }
        } else {
            setCouponError('Coupon codes are not supported for this plan');
        }
    };

    const getYearlyPlan = () => {
        return subscriptionPlans.find(plan => plan.interval === SubscriptionIntervals.YEAR);
    };

    const getMonthlyPlan = () => {
        return subscriptionPlans.find(plan => plan.interval === SubscriptionIntervals.MONTH);
    };

    const getSelectedPlan = () => {
        return subscriptionPlans.find(plan => plan.interval === interval);
    };

    const getSavingsPercentage = () => {
        const yearlyPlan = getYearlyPlan();
        return yearlyPlan ? yearlyPlan.savingsPercentage : 0;
    };

    const formatPrice = (price) => {
        if (!price) return price;
        return price.replace(/\.00$/, '');
    };

    const handlePlanSelect = (selectedInterval) => {
        setInterval(selectedInterval);
    };

    const getHeaderClass = () => {
        if (isFableticsEnabled && variant === FableticsVariant.VERSION_TWO) {
            return styles.versionTwoHeader;
        }
        if (isFableticsEnabled && variant === FableticsVariant.CONTROL) {
            return styles.controlHeader;
        }
        return styles.header;
    };

    const getHeaderTitle = () => {
        if (isCppContext) {
            return 'Upgrade to Annual Power';
        }
        if (isFableticsEnabled) {
            return 'Power pays for itself!';
        }
        return 'Power: The tennis membership that pays for itself.';
    };

    const renderDefaultLeftColumn = () => (
        <>
            <div className={styles.intro}>
                <div className={styles.introTitle} data-testid="modal.power-upgrade.intro-title">
                    You are one step away from elevating your game.
                </div>
            </div>
            <div className={styles.benefitsBox}>
                <div className={styles.benefitsBoxTitle} data-testid="modal.power-upgrade.benefits-title">
                    A Power membership unlocks:
                </div>
                <ul className={styles.benefitsList}>
                    <li data-testid="modal.power-upgrade.benefit-0">See precise 2 decimal ratings for all players</li>
                    <li data-testid="modal.power-upgrade.benefit-1">View in-depth performance analytics</li>
                    <li data-testid="modal.power-upgrade.benefit-2">Get discounted entry fees</li>
                    <li data-testid="modal.power-upgrade.benefit-3">Access advanced tools for college recruits</li>
                    <li data-testid="modal.power-upgrade.benefit-4">Discounts on travel, apparel, gear, and so much more</li>
                </ul>
            </div>
            {showHotelPlannerCard && (
                <div className={styles.hotelPlannerSection}>
                    <div className={styles.hotelPlannerImageWrapper}>
                        <img
                            src="/img/v3/graphics/perks/hotel-planner.jpg"
                            alt="Tennis court resort"
                            className={styles.hotelPlannerImage}
                            data-testid="modal.power-upgrade.hotel-img"
                        />
                    </div>
                    <div className={styles.hotelPlannerContent}>
                        <p className={styles.hotelPlannerHeadline} data-testid="modal.power-upgrade.hotel-headline">
                            Sign up for a Power annual plan and unlock:
                        </p>
                        <ul className={styles.hotelPlannerBenefits}>
                            <li data-testid="modal.power-upgrade.hotel-benefit-0">Over $250 of value in travel benefits</li>
                            <li data-testid="modal.power-upgrade.hotel-benefit-1">Up to 60% off on 1.4 million hotels globally</li>
                        </ul>
                        <button
                            type="button"
                            className={styles.hotelPlannerLearnMore}
                            onClick={() => window.open(links.powerPlayersPage, '_blank', 'noopener,noreferrer')}
                            data-testid="modal.power-upgrade.hotel-learn-more-btn"
                        >
                            <span className={styles.underline}>Learn more</span> about Global Privileges
                        </button>
                    </div>
                </div>
            )}
        </>
    );

    const renderLeftColumn = () => {
        if (LeftColumnVariant) {
            return <LeftColumnVariant />;
        }
        if (isFableticsEnabled) {
            return (
                <div className={styles.fableticsCreativeWrapper}>
                    <FableticsUpgradeCreative />
                </div>
            );
        }
        return renderDefaultLeftColumn();
    };

    const modalHeader = () => {
        const headerClass = getHeaderClass();
        return (
            <div className={`${popupStyles.modalHeader} ${headerClass}`}>
                <div className={styles.headerSpacer} />
                <p className={styles.headerTitle} data-testid="modal.power-upgrade.title">{getHeaderTitle()}</p>
                <button
                    type="button"
                    className={`${popupStyles.btnClose} ${styles.closeBtn}`}
                    onClick={props.onCloseClick}
                    aria-label="Close"
                    data-testid="modal.power-upgrade.close-btn"
                >
                    <i className="utr utr-close" aria-hidden />
                </button>
            </div>
        );
    };

    const renderPaymentCards = () => (
        <div className={styles.cards}>
            <span className={styles.cardsText} data-testid="modal.power-upgrade.cards-text">We accept all major credit cards.</span>
            <Img src="/img/v3/graphics/cards/amex-new.svg" data-testid="modal.power-upgrade.amex-img" />
            <Img src="/img/v3/graphics/cards/mastercard-new.svg" data-testid="modal.power-upgrade.mastercard-img" />
            <Img src="/img/v3/graphics/cards/visa-new.svg" className={styles.visaCard} data-testid="modal.power-upgrade.visa-img" />
            <Img src="/img/v3/graphics/cards/discover-new.svg" data-testid="modal.power-upgrade.discover-img" />
        </div>
    );

    const renderPlanSelection = () => (
        <div className={styles.planSection}>
            {showBothPlanOptions && <div className={styles.planTitle} data-testid="modal.power-upgrade.plan-title">Choose Your Plan:</div>}
            <div className={styles.planOptions}>
                <div
                    className={`${styles.planOption} ${interval === SubscriptionIntervals.YEAR ? styles.selected : ''}`}
                    onClick={() => handlePlanSelect(SubscriptionIntervals.YEAR)}
                    data-testid="modal.power-upgrade.annual-plan-option"
                >
                    <div className={styles.planDetails}>
                        <div className={styles.planName} data-testid="modal.power-upgrade.annual-plan-name">Annual Plan</div>
                        <div className={styles.planPrice} data-testid="modal.power-upgrade.annual-plan-price">
                            {formatPrice(getYearlyPlan()?.displayAmountPayPerRecurringInterval) || '$120'}
                        </div>
                        <div className={styles.planMonthly} data-testid="modal.power-upgrade.annual-plan-monthly-label">
                            {formatPrice(getYearlyPlan()?.displayMonthlyAmount) || '$10'}/Month - <span className={styles.bestValue} data-testid="modal.power-upgrade.best-value-label">BEST VALUE</span>
                        </div>
                    </div>
                    <div className={styles.planCheckmark}>
                        {interval === SubscriptionIntervals.YEAR ? (
                            <Img src="/img/v4/icon-checkmark.svg" />
                        ) : (
                            <div className={styles.emptyCheckmark} />
                        )}
                    </div>
                </div>
                <div
                    className={`${styles.planOption} ${interval === SubscriptionIntervals.MONTH ? styles.selected : ''} ${!showBothPlanOptions ? styles.disabled : ''}`}
                    onClick={() => showBothPlanOptions && handlePlanSelect(SubscriptionIntervals.MONTH)}
                    data-testid="modal.power-upgrade.monthly-plan-option"
                >
                    <div className={styles.planDetails}>
                        <div className={styles.planName} data-testid="modal.power-upgrade.monthly-plan-name">Monthly Plan</div>
                        <div className={styles.planPrice} data-testid="modal.power-upgrade.monthly-plan-price">
                            {formatPrice(getMonthlyPlan()?.displayAmountPayPerRecurringInterval) || '$12'}/Month
                        </div>
                        {isFableticsEnabled && (
                            <div className={styles.planExcludes} data-testid="modal.power-upgrade.monthly-plan-excludes">
                                Excludes Fabletics and Global Privileges
                            </div>
                        )}
                    </div>
                    <div className={styles.planCheckmark}>
                        {interval === SubscriptionIntervals.MONTH ? (
                            <Img src="/img/v4/icon-checkmark.svg" />
                        ) : (
                            <div className={styles.emptyCheckmark} />
                        )}
                    </div>
                </div>
            </div>
            {binCountryError && (
                <div className={styles.binCountryError}>
                    <div className={styles.errorMessage}>
                        <div className={styles.errorTitle} data-testid="modal.power-upgrade.bin-error-title">
                            Billing Country Does Not Match Your Residence
                        </div>
                        <div className={styles.errorText} data-testid="modal.power-upgrade.bin-error-text">
                            Our subscription pricing is based on your billing country. The rates have been updated accordingly.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className={`${styles.popup} overlay lite-black`}>
            <Popup
                className="popup-lg"
                config={{
                    size: 'lg',
                    scrollable: true,
                    customHeader: modalHeader,
                    customWidth: '900px',
                    bodyClassName: styles.modalBodyMobile,
                }}
                onCloseClick={props.onCloseClick}
            >
                <div className={styles.content}>
                    <div className={`${styles.leftColumn} ${!showHotelPlannerCard ? styles.noCard : ''}`}>
                        {renderLeftColumn()}
                    </div>
                    <div className={styles.rightColumn}>
                        <div className={styles.formSection}>
                            <div className={styles.planSelectionWrapper}>
                                {renderPlanSelection()}
                            </div>
                            <div className={styles.formTitle} data-testid="modal.power-upgrade.form-title">
                                Please enter your credit card information below.
                            </div>
                            <PowerCheckoutForm
                                interval={interval}
                                onComplete={handleSuccess}
                                hideHeader
                                themeV3
                                hideSaveCard
                                planData={getSelectedPlan()}
                                couponCode={couponCode}
                                couponError={couponError}
                                applyCoupon={applyCoupon}
                                invoiceItems={invoiceItems}
                                onBinCountryMismatch={handleBinCountryMismatch}
                                isLoadingPricing={isLoadingPricing}
                                submitButtonText="START POWER"
                                isTennisAustraliaMember={isTennisAustraliaMember}
                                savingsPercentage={getSavingsPercentage()}
                                referringSurface={props.referringSurface}
                                gatedFeature={props.gatedFeature}
                                // Slot rendered between the card fields and the pricing/submit
                                // row — the spot where the design wants the "We accept all
                                // major credit cards" line.
                                renderBeforeLineItem={renderPaymentCards}
                                prismicTitle={prismicTitle}
                            />
                            <div className={styles.disclaimer} data-testid="modal.power-upgrade.disclaimer">
                                By tapping the button, I agree to the <a href={`${config?.marketingAppHost}/policies/terms-of-service`} target="_blank" rel="noopener noreferrer" data-testid="modal.power-upgrade.terms-link">Terms of Service</a> and automatic charges until I cancel.
                            </div>
                        </div>
                    </div>
                </div>
            </Popup>
        </div>
    );
};

UpgradeToPowerPopupV4.propTypes = {
    onCloseClick: PropTypes.func.isRequired,
    showHotelPlannerCard: PropTypes.bool,
    popupType: PropTypes.string,
    referringSurface: PropTypes.string.isRequired,
    gatedFeature: PropTypes.string,
    prismicTitle: PropTypes.string,
};

UpgradeToPowerPopupV4.defaultProps = {
    showHotelPlannerCard: true,
    popupType: null,
    gatedFeature: null,
    prismicTitle: null,
};

export default UpgradeToPowerPopupV4;
